import React from 'react'
import { motion } from 'framer-motion'

const ServicesGallerySection: React.FC = () => {
  const serviceItems = [
    { title: "Seasonal Inspections", description: "Comprehensive quarterly assessments preventing issues before they arise" },
    { title: "Emergency Coordination", description: "24/7 rapid response with our trusted network of professionals" },
    { title: "Vendor Management", description: "Curated network of premium service providers at your disposal" }
  ]

  return (
    <section 
      className="relative py-0"
      style={{ 
        backgroundColor: 'rgb(246, 246, 241)', // Exact Figma background
        height: '600px'
      }}
    >
      {/* Top Divider Line */}
      <div 
        className="absolute top-0 left-0 right-0 h-px"
        style={{ backgroundColor: 'rgb(82, 76, 61)' }}
      />

      <div className="relative max-w-[1200px] mx-auto h-full overflow-hidden">
        
        {/* Left Content Section */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between py-12" style={{ width: '600px' }}>
          
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <p 
              className="text-lg font-medium tracking-wide mb-8"
              style={{ color: 'rgb(30, 29, 28)' }}
            >
              What We Do
            </p>
            
            <h2 
              className="text-4xl lg:text-5xl font-normal leading-tight mb-12"
              style={{ 
                color: 'rgb(30, 29, 28)',
                maxWidth: '313px',
                fontWeight: '400'
              }}
            >
              Home Management Services
            </h2>
          </motion.div>

          {/* Service Items */}
          <div className="flex-1 mb-16">
            <div className="space-y-8">
              {serviceItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + (index * 0.1) }}
                >
                  <h3 
                    className="text-xl font-semibold mb-2"
                    style={{ color: 'rgb(30, 29, 28)' }}
                  >
                    {item.title}
                  </h3>
                  <p 
                    className="text-base leading-relaxed"
                    style={{ color: 'rgb(82, 76, 61)' }}
                  >
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Bottom CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="relative"
          >
            {/* Divider Line */}
            <div 
              className="w-[121px] h-px mb-6"
              style={{ backgroundColor: 'rgb(30, 29, 28)' }}
            />
            
            {/* CTA Link */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="text-lg font-medium tracking-wide transition-all duration-200 hover:opacity-70"
              style={{ color: 'rgb(30, 29, 28)' }}
            >
              Learn More
            </motion.button>
          </motion.div>
        </div>

        {/* Vertical Divider */}
        <div 
          className="absolute h-full w-0.5 top-0"
          style={{ 
            left: '599px',
            backgroundColor: 'rgb(82, 76, 61)',
            height: '600px',
            maxWidth: '1px'
          }}
        />

        {/* Right Image Section */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="absolute right-0 top-0"
          style={{ 
            width: '960px', 
            height: '600px',
            left: '600px'
          }}
        >
          <div 
            className="w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: `url('/hero-image.png')`,
              backgroundSize: 'cover',
              backgroundPosition: '50% 35%'
            }}
          >
            {/* Overlay for better text readability if needed */}
            <div className="absolute inset-0 bg-black bg-opacity-10" />
            
            {/* Optional overlay content */}
            <div className="absolute bottom-8 left-8 text-white">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <p className="text-sm font-medium opacity-90 mb-2">
                  Featured Project
                </p>
                <p className="text-lg font-semibold">
                  Luxury Estate Management
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Responsive Layout */}
      <div className="lg:hidden px-4 py-12 h-auto min-h-[600px]">
        <div className="text-center mb-12">
          <p 
            className="text-lg font-medium tracking-wide mb-4"
            style={{ color: 'rgb(30, 29, 28)' }}
          >
            What We Do
          </p>
          <h2 
            className="text-3xl font-normal leading-tight mb-8"
            style={{ color: 'rgb(30, 29, 28)' }}
          >
            Home Management Services
          </h2>
        </div>

        {/* Mobile Image */}
        <div className="w-full h-64 bg-cover bg-center rounded-lg mb-8"
          style={{
            backgroundImage: `url('/hero-image.png')`,
            backgroundSize: 'cover',
            backgroundPosition: '50% 35%'
          }}
        />

        {/* Mobile Services */}
        <div className="space-y-6 mb-8">
          {serviceItems.map((item, index) => (
            <div key={index} className="text-center">
              <h3 
                className="text-lg font-semibold mb-1"
                style={{ color: 'rgb(30, 29, 28)' }}
              >
                {item.title}
              </h3>
              <p 
                className="text-base leading-relaxed"
                style={{ color: 'rgb(30, 29, 28)' }}
              >
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile CTA */}
        <div className="text-center">
          <div 
            className="w-32 h-px mx-auto mb-6"
            style={{ backgroundColor: 'rgb(30, 29, 28)' }}
          />
          <button
            className="text-lg font-medium tracking-wide"
            style={{ color: 'rgb(30, 29, 28)' }}
          >
            Learn More
          </button>
        </div>
      </div>
    </section>
  )
}

export default ServicesGallerySection 